<script setup lang="ts">
import { onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjects, type Project } from '@/composables/useApi'
import { useRouter } from 'vue-router'
import MemberHeader from '@/components/member/MemberHeader.vue'
import MemberSidebar from '@/components/member/MemberSidebar.vue'
import ProjectsTable from '@/components/member/ProjectsTable.vue'

const { t } = useI18n()

// Router for navigation
const router = useRouter()

// For demo purposes, using a placeholder tenantId
// In a real app, this would come from authentication/user context
const TENANT_ID = '3174acac-c3a7-4276-9ed3-f435a26a7720'

// Use the projects composable
const { projects, loading, error, stats, fetchProjects, updateStats } = useProjects(TENANT_ID)

// Fetch projects when component mounts
onMounted(async () => {
  await fetchProjects()
  updateStats()
})

// Watch projects and update stats when they change
watch(projects, () => {
  updateStats()
}, { deep: true })

// Project action handlers
const handleViewProject = (project: Project) => {
  console.log('View project:', project.projectName)
  // TODO: Navigate to project detail page
  // router.push(`/member/projects/${project.projectId}`)
}

const handleEditProject = (project: Project) => {
  console.log('Edit project:', project.projectName)
  // TODO: Navigate to project edit page
  // router.push(`/member/projects/${project.projectId}/edit`)
}

const handleSellSODAs = (project: Project) => {
  console.log('Sell SODAs for project:', project.projectName)
  // TODO: Open SODA selling modal or navigate to selling page
}

const handleCreateProject = () => {
  console.log('Create new project')
  // TODO: Navigate to project creation page
  // router.push('/member/projects/new')
}

const handleRefreshProjects = () => {
  fetchProjects()
}

// Sidebar action handlers
const handleSidebarAction = (actionType: string) => {
  switch (actionType) {
    case 'createProject':
      console.log('Create new project')
      // TODO: Navigate to project creation page or open modal
      break
    case 'createMethodology':
      console.log('Create new methodology')
      // TODO: Navigate to methodology creation page or open modal
      break
    default:
      console.log('Unknown action:', actionType)
  }
}
</script>

<template>

<MemberHeader />

<div class="flex h-screen">
    <!-- Shared Member Sidebar -->
    <MemberSidebar 
        active-item="projects" 
        @action="handleSidebarAction"
    />

    <main id="main-dashboard" class="flex-1 overflow-auto">
        <div class="p-8">
            <div id="page-header" class="mb-8">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-900">{{ $t('member.projects_page.header.title') }}</h1>
                        <p class="text-gray-600 mt-1">{{ $t('member.projects_page.header.subtitle') }}</p>
                    </div>
                    <button class="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                        <i class="fa-solid fa-plus mr-2"></i>
                        {{ $t('member.projects_page.header.new_project_button') }}
                    </button>
                </div>
            </div>

            <section id="projects-stats" class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div class="bg-white p-6 rounded-lg shadow-sm border">
                    <div class="flex items-center">
                        <div class="p-2 bg-blue-100 rounded-lg">
                            <i class="fa-solid fa-folder text-blue-600"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm text-gray-600">{{ $t('member.projects_page.stats.total_projects') }}</p>
                            <p class="text-2xl font-semibold text-gray-900">{{ stats.totalProjects }}</p>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white p-6 rounded-lg shadow-sm border">
                    <div class="flex items-center">
                        <div class="p-2 bg-green-100 rounded-lg">
                            <i class="fa-solid fa-check-circle text-green-600"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm text-gray-600">{{ $t('member.projects_page.stats.active_projects') }}</p>
                            <p class="text-2xl font-semibold text-gray-900">{{ stats.activeProjects }}</p>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white p-6 rounded-lg shadow-sm border">
                    <div class="flex items-center">
                        <div class="p-2 bg-purple-100 rounded-lg">
                            <i class="fa-solid fa-certificate text-purple-600"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm text-gray-600">{{ $t('member.projects_page.stats.sodas_issued') }}</p>
                            <p class="text-2xl font-semibold text-gray-900">{{ stats.totalAssetsMinted.toLocaleString() }}</p>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white p-6 rounded-lg shadow-sm border">
                    <div class="flex items-center">
                        <div class="p-2 bg-amber-100 rounded-lg">
                            <i class="fa-solid fa-dollar-sign text-amber-600"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm text-gray-600">{{ $t('member.projects_page.stats.available_for_sale') }}</p>
                            <p class="text-2xl font-semibold text-gray-900">{{ Math.round(stats.availableForSale).toLocaleString() }}</p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Shared Projects Table Component -->
            <ProjectsTable
                :projects="projects"
                :loading="loading"
                :error="error"
                :title="$t('member.projects_page.projects_table.title')"
                @refresh="handleRefreshProjects"
                @view-project="handleViewProject"
                @edit-project="handleEditProject"
                @sell-so-das="handleSellSODAs"
                @create-project="handleCreateProject"
            />

            <section id="milestone-activity" class="mt-8 bg-white rounded-lg shadow-sm border">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-semibold text-gray-900">{{ $t('member.projects_page.milestone_activity.title') }}</h2>
                </div>
                <div class="p-6">
                    <div class="space-y-4">
                        <div class="flex items-start space-x-3">
                            <div class="flex-shrink-0">
                                <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <i class="fa-solid fa-check text-green-600 text-xs"></i>
                                </div>
                            </div>
                            <div class="flex-1">
                                <p class="text-sm text-gray-900 font-medium">{{ $t('member.projects_page.milestone_activity.activities.sodas_issued') }}</p>
                                <p class="text-xs text-gray-500">{{ $t('member.projects_page.milestone_activity.activities.sodas_count') }}</p>
                            </div>
                        </div>
                        
                        <div class="flex items-start space-x-3">
                            <div class="flex-shrink-0">
                                <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <i class="fa-solid fa-upload text-blue-600 text-xs"></i>
                                </div>
                            </div>
                            <div class="flex-1">
                                <p class="text-sm text-gray-900 font-medium">{{ $t('member.projects_page.milestone_activity.activities.verification_uploaded') }}</p>
                                <p class="text-xs text-gray-500">{{ $t('member.projects_page.milestone_activity.activities.wind_project') }}</p>
                            </div>
                        </div>
                        
                        <div class="flex items-start space-x-3">
                            <div class="flex-shrink-0">
                                <div class="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                    <i class="fa-solid fa-clock text-amber-600 text-xs"></i>
                                </div>
                            </div>
                            <div class="flex-1">
                                <p class="text-sm text-gray-900 font-medium">{{ $t('member.projects_page.milestone_activity.activities.project_submitted') }}</p>
                                <p class="text-xs text-gray-500">{{ $t('member.projects_page.milestone_activity.activities.forest_project') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </main>
</div>
</template>

<!-- Scoped styles - only apply to this component -->
<style scoped>
/* Any component-specific styles would go here */
/* Scoped means these styles won't affect other components */
</style>